import { Controller, useForm, useFieldArray } from 'react-hook-form';
import { useNavigate, useLocation } from 'react-router-dom';
import { number, string, object, ref, array } from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import React from 'react';
import { useSuppliers } from '../../../../api/suppliers';
import { useCustomsDeliveryMethods } from '../../../../consts/db-values';
import {
  CleanCustomsDeliveryMethod,
  CustomsDeliveryMethodType,
} from '../../../../export-types/cleaned-types';
import { Input } from '../../../common/input.component';
import { required } from '../../../../utils/validations';
import { Select } from '../../../common/select.component';
import { AddButton } from '../../../common/add-button';
import { Header } from '../../../common/header.component';
import useSWRMutation from 'swr/mutation';
import { trpc, RouterInput } from '../../../../api/trpc';
import { AlertService } from '../../../../services/alert.service';

const transformNumber = (value: any) => {
  const parsed = parseFloat(value);
  return isNaN(parsed) ? undefined : parsed;
};
const floatNumber = number().transform(transformNumber);
const integerNumber = floatNumber.integer();
const integerNumberRequired = integerNumber.required('Заполните поле');

const floatNumberRequired = floatNumber.required('Заполните поле');
const stringRequired = string()
  .transform((value) => value.trim())
  .required('Заполните поле');

type Tariff = {
  price: string;
  weightTo: string;
  weightFrom: string;
};

const TariffSchema = object().shape({
  price: floatNumber.required('Заполните поле'),
  weightTo: floatNumber.moreThan(ref('weightFrom')),
  weightFrom: floatNumberRequired.test({
    name: 'weightFrom',
    skipAbsent: true,
    message: ({ index }) =>
      `Плотность в этом тарифе пересекается с другим интервалом (№${index})`,
    test: (valueFrom, context) => {
      const tariffs = context.from![1].value.tariffs;
      const fromTo = tariffs.map((item: Tariff, index: number) => {
        const from = parseFloat(item.weightFrom);
        const to = parseFloat(item.weightTo);

        return {
          index,
          from,
          to: isNaN(to) ? Infinity : to,
        };
      });
      const current = context.parent;
      const valueTo = current.weightTo || Infinity;
      // @ts-ignore context.options.index есть в InternalOptions
      const currentIndex = context.options.index;
      for (const { index, from, to } of fromTo) {
        if (index === currentIndex) {
          continue;
        }
        if (valueFrom < to && valueTo > from) {
          return context.createError({ params: { index: index + 1 } });
        }
      }
      return true;
    },
  }),
});
const FormSchema = object().shape({
  method: stringRequired,
  name: stringRequired,
  deliveryPeriodFrom: integerNumberRequired,
  deliveryPeriodTo: integerNumber.moreThan(ref('deliveryPeriodFrom')),
  tariffs: array().of(TariffSchema),
  supplierId: integerNumberRequired,
});

export const CustomsDeliveryMethodScreen = () => {
  const { data: suppliers } = useSuppliers();
  const DELIVERY_METHODS = useCustomsDeliveryMethods();

  const deliveryMethods = Object.keys(DELIVERY_METHODS).map((key) => ({
    label: DELIVERY_METHODS[key as keyof typeof DELIVERY_METHODS],
    value: key,
  }));

  const { trigger: createMethod } = useSWRMutation(
    ['customsDeliveryMethod.create'],
    (_k, { arg }: { arg: RouterInput['customsDeliveryMethod']['create'] }) =>
      trpc.customsDeliveryMethod.create.mutate(arg),
  );
  const { trigger: updateMethod } = useSWRMutation(
    ['customsDeliveryMethod.update'],
    (_k, { arg }: { arg: RouterInput['customsDeliveryMethod']['update'] }) =>
      trpc.customsDeliveryMethod.update.mutate(arg),
  );

  const navigate = useNavigate();
  const { state } = useLocation() as {
    state?: CleanCustomsDeliveryMethod;
  };

  const {
    register,
    control,
    formState: { errors },
    handleSubmit,
  } = useForm({
    // @ts-ignore
    defaultValues: {
      ...{
        tariffs: [
          {
            weightFrom: '',
            weightTo: '',
            price: '',
          },
        ],
      },
      ...state,
      supplierId: state?.supplier?.id,
    },
    // @ts-ignore
    resolver: yupResolver(FormSchema),
  });
  const { fields, append, remove } = useFieldArray({
    control,
    name: 'tariffs',
  });

  function onSave(deliveryMethod: {
    name: string;
    tariffs?: {
      price: number;
      weightFrom: number;
      weightTo?: number;
    }[];
    disabled?: boolean;
    supplierId: number;
    method: string;
    deliveryPeriodFrom: number;
    deliveryPeriodTo?: number;
  }) {
    const processedData = {
      ...deliveryMethod,
      disabled: !!deliveryMethod.disabled,
      tariffs: deliveryMethod.tariffs?.map((tariff) => ({
        ...tariff,
        price: +tariff.price,
        densityFrom: +tariff.weightFrom,
        densityTo: tariff.weightTo ? +tariff.weightTo : undefined,
      })),
      method:
        CustomsDeliveryMethodType[
          deliveryMethod.method as keyof typeof DELIVERY_METHODS
        ],
    };
    const promise = state?.id
      ? updateMethod({ id: state?.id, data: processedData })
      : createMethod({ ...processedData });
    promise.then(() => {
      AlertService.success();
      navigate('/customs-deliveries/delivery-methods');
    });
  }

  const isEdit = !!state;

  return (
    <>
      <Header />
      <div className="container" style={{ marginTop: -70 }}>
        <h1 className="mb-4">
          {isEdit ? 'Изменить' : 'Добавить'} способ доставки
        </h1>
        <form onSubmit={handleSubmit(onSave)}>
          <div className="row">
            <Controller
              name="supplierId"
              control={control}
              rules={required}
              render={({ field }) => (
                <Select
                  className="mb-3"
                  title="Поставщик"
                  placeholder="Выберите поставщика"
                  options={
                    suppliers?.map(({ id, name, location }) => ({
                      value: id,
                      label:
                        name +
                        (location?.name ? ` (откуда: ${location.name})` : ''),
                    })) ?? []
                  }
                  {...field}
                  error={errors.supplierId?.message}
                />
              )}
            />
            <div className="col-12 mb-3">
              <Controller
                name="method"
                control={control}
                render={({ field }) => (
                  <Select
                    {...field}
                    options={deliveryMethods}
                    title="Способ доставки"
                    placeholder="Способ доставки"
                    error={errors.method?.message}
                  />
                )}
              />
            </div>
            <div className="col-6 mb-3">
              <Input
                title="Название тарифа"
                placeholder="Например, Авиа (Экспресс)"
                {...register('name')}
                error={errors.name?.message}
              />
              <div className="fs-14 color-gray-400">
                Это название отобразится у клиента
              </div>
            </div>
            <div className="col-6 mb-3">
              <div className="mb-2">Срок доставки</div>
              <div className="d-flex flex-row align-items-center">
                <Input
                  className="flex-grow-1"
                  type="number"
                  min={0}
                  step={1}
                  {...register('deliveryPeriodFrom')}
                  error={errors.deliveryPeriodFrom?.message}
                  postfix="дней"
                />
                <div className="ps-2 pe-2">—</div>
                <Input
                  className="flex-grow-1"
                  type="number"
                  min={0}
                  step={1}
                  {...register('deliveryPeriodTo')}
                  error={errors.deliveryPeriodTo?.message}
                  postfix="дней"
                />
              </div>
              <div className="fs-14 color-gray-400">
                Точное количество дней или диапазон
              </div>
            </div>

            <h3>Тарифная сетка</h3>
            {fields.map((field, index) => (
              <div className="col-12 mb-3" key={field.id}>
                <div className="row align-items-start">
                  <div className="col-8">
                    <div className="mb-2">№{index + 1} Объем груза</div>
                    <div className="d-flex flex-row align-items-center">
                      <Input
                        className="flex-grow-1"
                        {...register(`tariffs.${index}.weightFrom`)}
                        type="number"
                        min={0}
                        step={0.01}
                        error={errors.tariffs?.[index]?.weightFrom?.message}
                        postfix="м³"
                      />
                      <div className="ps-2 pe-2">—</div>
                      <Input
                        className="flex-grow-1"
                        {...register(`tariffs.${index}.weightTo`)}
                        type="number"
                        min={0}
                        step={0.01}
                        error={errors.tariffs?.[index]?.weightTo?.message}
                        postfix="м³"
                      />
                    </div>
                  </div>
                  <div className="col-4">
                    <div className="mb-2">Стоимость</div>
                    <div className="d-flex flex-row align-items-center">
                      <Input
                        className="flex-grow-1"
                        {...register(`tariffs.${index}.price`)}
                        type="number"
                        min={0}
                        step={0.01}
                        error={errors.tariffs?.[index]?.price?.message}
                        postfix="$ / м³"
                      />
                      {index > 0 ? (
                        <i
                          className="bi bi-trash p-2 cursor-pointer hover-red"
                          onClick={() => remove(index)}
                        />
                      ) : (
                        <div style={{ width: 31 }} />
                      )}
                    </div>
                  </div>
                </div>
              </div>
            ))}
            <div className="col-12">
              <AddButton
                onClick={() =>
                  // @ts-ignore
                  append({ price: '', densityTo: '', densityFrom: '' })
                }
              >
                Добавить тариф
              </AddButton>
            </div>
            <div className="col-12 pt-3 mb-4">
              <button type="submit" className="btn btn-success w-100">
                Сохранить
              </button>
            </div>
          </div>
        </form>
      </div>
    </>
  );
};
